import { useEffect, useId, useRef, useState } from "react";
import type { FocusEvent, JSX, KeyboardEvent } from "react";
import { Link } from "react-router";
import type { To } from "react-router";
import styles from "./AccountMenu.module.css";
import { cx } from "./classNames";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

/** Common part of an entry of the {@link AccountMenu}. */
interface AccountMenuEntry {
  /** Stable key of the entry. */
  readonly key: string;
  /** Visible label, also the accessible name. */
  readonly label: string;
  /** Icon in front of the label. */
  readonly icon: IconName;
}

/** An entry that leads to a page, for example the settings. */
export interface AccountMenuLink extends AccountMenuEntry {
  /** Destination. */
  readonly to: To;
}

/** An entry that runs an action, for example the sign-out. */
export interface AccountMenuAction extends AccountMenuEntry {
  /** Runs the action; the menu is closed and the focus is back on its button before. */
  readonly onSelect: () => void;
}

/** An entry of the {@link AccountMenu}. */
export type AccountMenuItem = AccountMenuLink | AccountMenuAction;

/** Properties of {@link AccountMenu}. */
export interface AccountMenuProps {
  /** Name of the signed-in person, shown on the button (shortened when room is short). */
  readonly name: string;
  /** Accessible name of the button; it contains the shown name (WCAG 2.5.3). */
  readonly label: string;
  /** Line above the entries, for example „Angemeldet als Erika Muster“. */
  readonly caption: string;
  /** The entries in display order. */
  readonly items: readonly AccountMenuItem[];
  /** Class of the root element, for its place in the app bar. */
  readonly className?: string | undefined;
}

/** Entry that receives the focus when the menu opens. */
type InitialFocus = "first" | "last";

/**
 * Returns the entries of an open menu in display order.
 *
 * @param menu - The menu element.
 * @returns The focusable entries.
 */
function menuItems(menu: HTMLElement | null): HTMLElement[] {
  const items = menu === null ? [] : [...menu.querySelectorAll<HTMLElement>('[role="menuitem"]')];
  return items;
}

/**
 * Returns the index an arrow, Home or End key moves the focus to, wrapping around at both ends.
 *
 * @param key - The pressed key.
 * @param current - Index of the focused entry, `-1` if none.
 * @param count - Number of entries.
 * @returns The new index, or `null` for any other key.
 */
export function nextMenuIndex(key: string, current: number, count: number): number | null {
  let next: number | null = null;

  if (key === "ArrowDown") {
    next = (current + 1) % count;
  } else if (key === "ArrowUp") {
    next = (current - 1 + count) % count;
  } else if (key === "Home") {
    next = 0;
  } else if (key === "End") {
    next = count - 1;
  }

  return next;
}

/**
 * Account menu at the name in the app bar (F-74): a menu button after the WAI-ARIA menu button
 * pattern. The button carries `aria-haspopup="menu"` and `aria-expanded`; click, Enter, Space and
 * Arrow Down open the menu on its first entry, Arrow Up on its last. Inside, the arrow keys move
 * round the entries, Home and End jump to the ends, Escape and Shift+Tab close it and return the
 * focus to the button, Tab moves on to the next control and closes it, as a click or tap outside
 * does. Choosing an entry closes the menu and returns the
 * focus to the button before a page opens (whose heading then takes the focus) or an action runs.
 * The entries are rendered only while the menu is open.
 *
 * @param props - Component properties.
 * @returns The menu button with its menu.
 */
export function AccountMenu(props: AccountMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const initialFocusRef = useRef<InitialFocus>("first");
  const buttonId = useId();
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const items = menuItems(menuRef.current);
    const target = initialFocusRef.current === "first" ? items[0] : items.at(-1);
    target?.focus();
    // A click or tap outside closes the menu and leaves the focus where the person put it.
    const closeOutside = (event: PointerEvent): void => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    const unsubscribe = (): void => {
      document.removeEventListener("pointerdown", closeOutside);
    };

    return unsubscribe;
  }, [open]);

  const openAt = (focus: InitialFocus): void => {
    initialFocusRef.current = focus;
    setOpen(true);
  };
  const close = (): void => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openAt(event.key === "ArrowDown" ? "first" : "last");
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };
  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    const items = menuItems(menuRef.current);
    const current = items.findIndex((item) => item === document.activeElement);
    const next = nextMenuIndex(event.key, current, items.length);

    if (next !== null) {
      event.preventDefault();
      items[next]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab" && event.shiftKey) {
      // Back to the button, where Shift+Tab would land anyway; Tab leaves through the blur below.
      event.preventDefault();
      close();
    }
  };
  const onMenuBlur = (event: FocusEvent<HTMLUListElement>): void => {
    const next = event.relatedTarget;

    if (next instanceof Node && rootRef.current?.contains(next) !== true) {
      setOpen(false);
    }
  };

  const menu = (
    <div ref={rootRef} className={cx(styles["root"], props.className)}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className={styles["button"]}
        aria-label={props.label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            openAt("first");
          }
        }}
        onKeyDown={onButtonKeyDown}
      >
        <Icon name="account" className={styles["icon"]} />
        <span className={styles["name"]}>{props.name}</span>
        <Icon name="chevronDown" className={styles["chevron"]} />
      </button>
      {open ? (
        <div className={styles["popup"]}>
          <p className={styles["caption"]}>{props.caption}</p>
          <ul
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-labelledby={buttonId}
            className={styles["list"]}
            onKeyDown={onMenuKeyDown}
            onBlur={onMenuBlur}
          >
            {props.items.map((item) => (
              <li key={item.key} role="none">
                {"to" in item ? (
                  <Link
                    role="menuitem"
                    tabIndex={-1}
                    className={styles["item"]}
                    to={item.to}
                    onClick={close}
                  >
                    <Icon name={item.icon} className={styles["icon"]} />
                    <span>{item.label}</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={styles["item"]}
                    onClick={() => {
                      close();
                      item.onSelect();
                    }}
                  >
                    <Icon name={item.icon} className={styles["icon"]} />
                    <span>{item.label}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );

  return menu;
}
